import type { SupabaseClient } from "@supabase/supabase-js";

export type CoachSubscriptionStatus =
  | "pending"
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "unpaid"
  | "incomplete"
  | "incomplete_expired"
  | "paused";

export type CoachSubscriptionTier = "coach_plus";

export interface CoachSubscription {
  userId: string;
  status: CoachSubscriptionStatus;
  tier: CoachSubscriptionTier;
  cancelAtPeriodEnd: boolean;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
  isEntitled: boolean;
  updatedAt: string | null;
}

export type CoachEntitlementSurface = "default" | "arc_coach";

/**
 * Read the caller's coach subscription status via the
 * `coach_subscription_status` view. Returns `null` when no row exists
 * (the coach has never touched billing yet). RLS ensures the caller
 * only sees their own row.
 */
export async function getCoachSubscription(
  client: SupabaseClient,
): Promise<CoachSubscription | null> {
  const { data, error } = await client
    .from("coach_subscription_status")
    .select(
      "user_id,status,tier,cancel_at_period_end,current_period_end,trial_end,is_entitled,updated_at",
    )
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    userId: String(data.user_id),
    status: (data.status as CoachSubscriptionStatus) ?? "pending",
    tier: (data.tier as CoachSubscriptionTier) ?? "coach_plus",
    cancelAtPeriodEnd: Boolean(data.cancel_at_period_end ?? false),
    currentPeriodStart: null,
    currentPeriodEnd: (data.current_period_end as string | null) ?? null,
    trialEnd: (data.trial_end as string | null) ?? null,
    isEntitled: Boolean(data.is_entitled ?? false),
    updatedAt: (data.updated_at as string | null) ?? null,
  };
}

/**
 * Create a Stripe Checkout Session via the `coach-checkout-session`
 * edge function and return the hosted URL the browser should redirect
 * to. `returnPath` is an optional same-origin path to land on after
 * checkout completes or is cancelled (default: "/billing").
 */
export async function startCoachCheckout(
  client: SupabaseClient,
  options: { returnPath?: string } = {},
): Promise<string> {
  const { data, error } = await client.functions.invoke<{
    ok: boolean;
    url?: string;
    session_id?: string;
    error?: string;
  }>("coach-checkout-session", {
    body: { return_path: options.returnPath ?? "/billing" },
  });
  if (error) throw new Error(error.message);
  if (!data?.ok || !data.url) {
    throw new Error(data?.error ?? "Checkout URL unavailable.");
  }
  return data.url;
}

/**
 * Open the Stripe Customer Portal for the current coach. Throws when
 * the coach has never completed checkout (no Stripe customer yet) —
 * the caller should route them to the Checkout flow instead.
 */
export async function openCoachBillingPortal(
  client: SupabaseClient,
  options: { returnPath?: string } = {},
): Promise<string> {
  const { data, error } = await client.functions.invoke<{
    ok: boolean;
    url?: string;
    error?: string;
  }>("coach-portal-session", {
    body: { return_path: options.returnPath ?? "/billing" },
  });
  if (error) throw new Error(error.message);
  if (!data?.ok || !data.url) {
    throw new Error(data?.error ?? "Billing portal URL unavailable.");
  }
  return data.url;
}

/**
 * One-line upsell copy shown by `EntitlementBanner` when a coach is
 * on a non-entitled surface. Varies by status so a canceled coach
 * sees different copy than a never-subscribed one.
 */
export function coachEntitlementUpsellCopy(
  subscription: CoachSubscription | null,
  options: { surface?: CoachEntitlementSurface } = {},
): string {
  const surface = options.surface ?? "default";
  const nounPhrase =
    surface === "arc_coach"
      ? "AI coaching recommendations and student workspaces"
      : "student workspaces";
  const pitch =
    surface === "arc_coach"
      ? "Subscribe to Coach+ to unlock AI coaching recommendations, build your Library, prescribe drills, and open student workspaces."
      : "Subscribe to Coach+ to build your Library, prescribe drills, and open student workspaces.";

  if (!subscription || subscription.status === "pending") {
    return pitch;
  }
  switch (subscription.status) {
    case "past_due":
      return surface === "arc_coach"
        ? "Your card needs attention. Coach+ keeps AI coaching recommendations available through the grace window."
        : "Your card needs attention. Coach+ stays available through the grace window.";
    case "canceled":
      return `Your Coach+ access ended. Restart to reopen ${nounPhrase}.`;
    case "unpaid":
      return surface === "arc_coach"
        ? "Stripe flagged this subscription as unpaid. Update your payment method to restore Coach+ and AI coaching recommendations."
        : "Stripe flagged this subscription as unpaid. Update your payment method to restore Coach+.";
    case "incomplete":
    case "incomplete_expired":
      return surface === "arc_coach"
        ? "Checkout didn't finish. Resume to activate Coach+ and AI coaching recommendations."
        : "Checkout didn't finish. Resume to activate Coach+.";
    case "paused":
      return `Coach+ is paused. Resume billing to reopen ${nounPhrase}.`;
    default:
      return surface === "arc_coach"
        ? "Subscribe to Coach+ to unlock AI coaching recommendations on this surface."
        : "Subscribe to Coach+ to unlock this surface.";
  }
}

/**
 * Human-readable label for a subscription status. Keep copy short —
 * these render in the billing card header and in the CoachTabBar pill.
 */
export function coachSubscriptionStatusLabel(
  status: CoachSubscriptionStatus,
): string {
  switch (status) {
    case "active":
      return "Active";
    case "trialing":
      return "Trial";
    case "past_due":
      return "Past due";
    case "canceled":
      return "Canceled";
    case "unpaid":
      return "Unpaid";
    case "incomplete":
      return "Incomplete";
    case "incomplete_expired":
      return "Expired";
    case "paused":
      return "Paused";
    case "pending":
    default:
      return "Not started";
  }
}
